"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Specifications() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: card1Ref,
    isVisible: card1Visible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: card2Ref,
    isVisible: card2Visible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: quoteRef,
    isVisible: quoteVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const domains = [{
    number: "01",
    label: "RULE LAYER",
    title: "A deterministic layer that resolves what it can, for free.",
    description: "Every student step is checked against the mark scheme's expected forms and a library of known error patterns. When it matches, the classification is free, instant, and fully interpretable, no model call required.",
    aspect: "aspect-[4/5]",
    to: "/RuleLayerPage"
  }, {
    number: "02",
    label: "MODEL ESCALATION",
    title: "Ambiguous cases are escalated, never guessed.",
    description: "When a step can't be resolved deterministically, it's batched and handed to a prompted language-model classifier that uses the student's full working to judge whether it's a procedural slip or a genuine gap in method.",
    aspect: "aspect-[5/4]",
    to: "/ModelEscalationPage"
  }];
  return <section data-section-id="3035" id="api-specifications" className="relative bg-background text-foreground py-24 md:py-32 lg:py-44 overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-y-8 mb-20 md:mb-28 lg:mb-32 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-start-2 lg:col-span-10 flex items-center gap-3 mb-4">
            <span className="inline-block w-2 h-2 rounded-md bg-primary" />
            <span className="font-mono text-xs tracking-[0.25em] uppercase text-muted-foreground">
              Technical Specs
            </span>
          </div>
          <h1 className="lg:col-start-2 lg:col-span-8 font-default text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-foreground">Two pillars. One <em className="italic text-primary font-medium">standard&nbsp;</em> for performance.</h1>
        </div>

        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-y-16 lg:gap-y-0 lg:gap-x-8">
          <div ref={card1Ref} data-index={0} className={`lg:col-start-2 lg:col-span-5 group transition-all duration-1000 ease-out ${card1Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"} ml-0 lg:ml-0`}>
            <CardItem item={domains[0]} />
          </div>

          <div className="hidden lg:block lg:col-start-7 lg:col-span-1 absolute left-1/2 top-12 bottom-12 w-px bg-border/60 pointer-events-none" style={{
          left: "50%"
        }} aria-hidden="true" />

          <div ref={quoteRef} className={`hidden lg:flex lg:col-start-7 lg:col-span-6 lg:row-start-1 items-start justify-end pt-4 pr-4 transition-all duration-1000 ease-out delay-300 ${quoteVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <p className="font-default italic text-xl xl:text-2xl leading-snug text-muted-foreground max-w-xs text-right">
              Engineered for precision, built for scale
            </p>
          </div>

          <div ref={card2Ref} data-index={1} className={`lg:col-start-7 lg:col-span-5 group lg:mt-[120px] lg:ml-0 transition-all duration-1000 ease-out delay-150 ${card2Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <CardItem item={domains[1]} />
          </div>
        </div>
      </div>
    </section>;
}
function CardItem({
  item
}: {
  item: {
    number: string;
    label: string;
    title: string;
    description: string;
    aspect: string;
    to: string;
  };
}) {
  return <Link to={item.to} className="block transition-transform duration-700 ease-out hover:-translate-y-1.5">
      <div className={`relative w-full ${item.aspect} overflow-hidden rounded-lg bg-card border border-border shadow-md flex flex-col transition-transform duration-[800ms] ease-out group-hover:scale-[1.03]`}>
        {/* Panel header bar */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-secondary border-b border-border select-none">
          <div className="flex items-center gap-1.5">
            <span className="size-2 rounded-lg bg-muted-foreground" />
            <span className="size-2 rounded-lg bg-muted-foreground" />
            <span className="size-2 rounded-lg bg-muted-foreground" />
          </div>
          <span className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground">
            {item.label}
          </span>
        </div>

        {/* Panel code body */}
        <div className="p-4 sm:p-5 font-mono text-xs text-muted-foreground/70 space-y-2.5 overflow-hidden flex-1 bg-card">
          {item.label === "RULE LAYER" ? <>
              <div className="w-3/4 truncate text-primary/80">// classifier/extraction.py: rule tier</div>
              <div className="w-11/12 truncate text-foreground/80">result = classify(scheme, student_id, steps)</div>
              <div className="w-4/5 truncate">if result.steps[i].source == &quot;rule&quot;:</div>
              <div className="w-2/3 truncate pl-4 text-foreground/90">return result.steps[i].label</div>
              <div className="w-5/6 truncate text-muted-foreground/60">// correct | process_error | knowledge_gap</div>
              <div className="w-4/5 truncate text-muted-foreground/60">// Zero latency, zero API cost when a rule matches.</div>
            </> : <>
              <div className="w-3/4 truncate text-primary/80">// classifier/extraction.py: LLM fallback</div>
              <div className="w-11/12 truncate text-foreground/80">if result.steps[i].source == &quot;llm&quot;:</div>
              <div className="w-2/3 truncate pl-4 text-foreground/90">return result.steps[i].label</div>
              <div className="w-5/6 truncate text-muted-foreground/60">// Escalated via classifier.baseline</div>
              <div className="w-5/6 truncate text-muted-foreground/60">// Full student working evaluated for context</div>
              <div className="w-4/5 truncate">assert result.steps[i].source in (&quot;rule&quot;, &quot;llm&quot;)</div>
            </>}
        </div>
      </div>

      <div className="mt-8 flex flex-col gap-3">
        <span className="font-default text-2xl text-primary leading-none">
          {item.number}
        </span>
        <span className="font-mono text-xs tracking-[0.25em] uppercase text-muted-foreground">
          {item.label}
        </span>
        <h3 className="font-default text-3xl md:text-4xl leading-tight tracking-tight text-foreground">
          {item.title}
        </h3>
        <p className="font-default text-base leading-relaxed text-muted-foreground max-w-md mt-1">
          {item.description}
        </p>
        <Button variant="link" asChild className="mt-2 px-0 h-auto self-start text-foreground hover:text-primary group/btn">
          <span className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase">
            View documentation
            <ArrowRight className="size-4 transition-transform duration-500 ease-out group-hover/btn:translate-x-1.5" />
          </span>
        </Button>
      </div>
    </Link>;
}